/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Else, If, Then, Unless, When } from "react-if";
import {
  Button,
  Description,
  Dropdown,
  Input,
  Label,
  Sidebar,
  theme,
} from "@nordcloud/gnui";
import {
  Role,
  UserPermissions,
  useSetUserPermissionsMutation,
} from "~/generated/graphql";
import { Fieldset, LoaderWrap } from "~/components";
import { useCustomers } from "~/services/customers";
import { handleError, toInputError, isEmpty } from "~/tools";
import { showRole } from "~/views/settings/UsersAndPermissions/showRole";
import { UsersAndPermissionsSchema } from "~/views/settings/validators/usersAndPermissions";
import { Account } from "../types";
import { ApplicationList } from "./ApplicationList";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onChange: () => void;
  editableData: Account | null;
};

export function AddUserPermissionsSidebar({
  isOpen,
  onClose,
  onChange,
  editableData,
}: Props) {
  const { isAdminLite } = useCustomers();

  const [userRole, setUserRole] = useState<Role>(
    isAdminLite ? Role.AdminLite : Role.Admin
  );

  const [selectedApplications, setSelectedApplications] = useState<string[]>(
    []
  );

  const {
    register,
    handleSubmit,
    setValue,
    reset,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(UsersAndPermissionsSchema),
  });

  const [setUserPermissions, { loading: isSetUserPermissionsLoading }] =
    useSetUserPermissionsMutation();

  const onSubmit = async ({ email }: UserPermissions) => {
    const inputApplications =
      userRole === Role.Admin ? [] : selectedApplications;

    try {
      await setUserPermissions({
        variables: {
          email,
          input: {
            role: userRole,
            applications: inputApplications,
          },
        },
      });

      reset();
      clearApplications();
      onChange();
    } catch (err) {
      handleError(toInputError(err));
    }
  };

  const isEditMode = editableData != null;

  useEffect(() => {
    if (isEditMode && isOpen) {
      const initialSelectedApplications =
        editableData.access.applicationsWithNames?.map(({ nid }) => nid) ?? [];

      setSelectedApplications(initialSelectedApplications);
      setValue("email", editableData.email);
      setUserRole(
        editableData.access.role === "ADMIN" ? Role.Admin : Role.TeamLead
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableData?.email, isOpen]);

  const clearApplications = useCallback(() => {
    setSelectedApplications([]);
  }, []);

  const onCloseSidebar = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const isPermissionsRequired = userRole === Role.TeamLead;

  return (
    <Sidebar
      isOpen={isOpen}
      title={isEditMode ? "Edit permissions" : "Add new User"}
      footer={
        <Button
          m={theme.spacing.spacing04}
          ml="auto"
          type="submit"
          form="permissionsForm"
          disabled={isPermissionsRequired && isEmpty(selectedApplications)}
        >
          Apply
        </Button>
      }
      onClick={onCloseSidebar}
    >
      <LoaderWrap inContent loading={isSetUserPermissionsLoading}>
        <form id="permissionsForm" onSubmit={handleSubmit(onSubmit)}>
          <Fieldset>
            <Label required name="Email" />
            <If condition={isEditMode}>
              <Then>
                <Input
                  disabled={isEditMode}
                  defaultValue={editableData?.email}
                />
                <input
                  type="hidden"
                  {...register("email", { required: true })}
                  defaultValue={editableData?.email}
                />
              </Then>
              <Else>
                <Input
                  type="text"
                  {...register("email", { required: true })}
                  defaultValue=""
                  placeholder="Email"
                />
                {errors.email && <Description>Email is required.</Description>}
              </Else>
            </If>
          </Fieldset>
          <Unless condition={isAdminLite}>
            <Fieldset>
              <Label required name="User Role" />
              <Dropdown
                value={userRole}
                name="Select User Role"
                options={userOptions}
                title="Select User Role"
                onChange={(newValue) => setUserRole(newValue as Role)}
              />
              {errors.role && <Description>Role is required.</Description>}
            </Fieldset>
          </Unless>
          <When condition={isPermissionsRequired}>
            <ApplicationList
              selectedApplications={selectedApplications}
              onSelectApplications={setSelectedApplications}
              onClear={clearApplications}
            />
          </When>
        </form>
      </LoaderWrap>
    </Sidebar>
  );
}

const userOptions = [
  { value: Role.Admin, label: showRole(Role.Admin) },
  { value: Role.TeamLead, label: showRole(Role.TeamLead) },
];
